// eslint-disable-next-line no-restricted-imports -- This is a wrapper around the datadog/browser-logs package
import { datadogLogs } from '@datadog/browser-logs';
import axios from 'axios';

import { ENVIRONMENTS, getEnvFromHostname } from 'pages/utils/getEnvFromHostname';
import Modules from 'shared_modules/Modules';

let isInitialized = false;

function init(forceInit = false): void {
  if (!Modules.hasModule('monitoring.datadog.logs')) return;

  if (!window.DD) {
    console.error('DD object not found in window, Datadog Logs could not be initialized.');
    return;
  }

  // forceInit should only be used in tests
  if (isInitialized && !forceInit) {
    return;
  }
  const env = getEnvFromHostname();

  /**
   * WARNING: sessionSampleRate must be 100 on PRODUCTION to avoid affecting Payment Terminal Monitoring.
   * Datadog Logs maintains a session in a `_dd_s` cookie that is not unique per Datadog Logs instance.
   */
  let sessionSampleRate = env !== ENVIRONMENTS.DEVELOPMENT ? 100 : 0;

  // This should only have an effect in development since other environments have a sample rate of 100
  if (window.DD.sampleOverwrite) {
    sessionSampleRate = 100;
  }

  /**
   * This will prevent console.error() from being automatically captured.
   * This is to avoid flooding Datadog Logs with errors that we don't explicitly care about.
   * This functionality is enabled in Datadog RUM. There you can have visibility on uncaught errors.
   */
  const forwardErrorsToLogs = false;

  datadogLogs.init({
    clientToken: window.DD.clientToken,
    site: window.DD.site,
    service: window.DD.service,
    env,
    sessionSampleRate,
    forwardErrorsToLogs,
  });

  isInitialized = true;
}

const debugLsidEvent = (message: string, data: Record<string, unknown>): void => {
  if (!Modules.hasModule('lsid.monitoring')) return;
  datadogLogs.logger.debug(`[LsidEvent]: ${message}`, data);
};

export type logErrorContext = Omit<
  {
    message?: string;
    source?: string;
    [key: string]: any;
  },
  'error | request | response'
>;

const logError = (error: unknown, context: logErrorContext = {}): void => {
  if (!context.source) context.source = 'n/a';

  const message = ((): string => {
    if (context.message) return context.message;
    if (error instanceof Error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.message) return error.response.data.message;
      }
      return error.message;
    }
    if (typeof error === 'string') return error;
    return 'Unknown error occurred';
  })();

  const augmentedContext: Record<string, any> = {
    ...context,
  };

  if (error instanceof Error) {
    augmentedContext.error = {
      message: error.message,
      stack: error.stack,
      name: error.name,
    };
  }

  if (error instanceof Error && axios.isAxiosError(error)) {
    augmentedContext.request = {
      method: error.config?.method,
      url: [error.config?.baseURL, error.config?.url].filter(Boolean).join(''),
    };
    if (error.response?.data) {
      augmentedContext.response = {
        status: error.response.status,
        data: error.response.data,
      };
    }
  }

  datadogLogs.logger.error(message, augmentedContext, error instanceof Error ? error : undefined);

  if (getEnvFromHostname() === ENVIRONMENTS.DEVELOPMENT)
    console.debug('logError:', message, augmentedContext, error);
};

const randomLogId = (): string => Math.random().toString(36).slice(2);

const datadogLogsInit = init;

export { datadogLogs, datadogLogsInit, randomLogId, debugLsidEvent, logError };
